import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageIntro, Card, StatCard } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Brain, Lock, Save, Loader2, RefreshCw, Pencil, Trophy, Download,
  ShieldCheck, Clock, CheckCircle2, AlertTriangle, Info, ChevronDown, ChevronRight,
  Users, Search, X, FlaskConical, Calendar, History, ExternalLink, Power,
} from "lucide-react";

const TZ_NAME = "America/Mazatlan";
const TZ_LABEL = "Los Cabos time";
const MONTHS3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Wall-clock scheduling value (naive, already in event/Los Cabos time) -> readable.
// Never runs through Date(), so it can't be shifted by the manager's browser tz.
function fmtLocal(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  const [, y, mo, d, hh, mm] = m;
  let h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const mins = mm === "00" ? "" : `:${mm}`;
  return `${MONTHS3[parseInt(mo, 10) - 1]} ${parseInt(d, 10)}, ${y} · ${h}${mins} ${ampm} ${TZ_LABEL}`;
}

// Audit instant (true UTC/ISO timestamp) -> shown explicitly in Los Cabos time.
function fmtInstant(iso) {
  if (!iso) return null;
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone: TZ_NAME, dateStyle: "medium", timeStyle: "short",
    }).format(new Date(iso));
    return `${f} ${TZ_LABEL}`;
  } catch { return iso; }
}

const TABS = [
  { key: "setup", label: "Setup & rounds" },
  { key: "questions", label: "Questions" },
  { key: "finalize", label: "Prizes & finalise" },
  { key: "standings", label: "Standings" },
];

const LIFECYCLE_STYLE = {
  draft: "bg-[hsl(var(--muted)/0.6)] text-[hsl(var(--muted-foreground))]",
  scheduled: "bg-[hsl(214_70%_94%)] text-[hsl(214_60%_34%)]",
  open: "bg-[hsl(142_45%_92%)] text-[hsl(142_45%_28%)]",
  paused: "bg-[hsl(38_70%_92%)] text-[hsl(38_55%_32%)]",
  closed: "bg-[hsl(28_80%_92%)] text-[hsl(28_60%_32%)]",
  finalised: "bg-[hsl(265_50%_93%)] text-[hsl(265_45%_38%)]",
};

function QuestionEditor({ eventId, q, locked, onSaved, patchFn, lockLabel }) {
  const [prompt, setPrompt] = useState(q.prompt);
  const [options, setOptions] = useState(q.options.map((o) => ({ ...o })));
  const [correct, setCorrect] = useState(q.correct_option_id);
  const [explanation, setExplanation] = useState(q.explanation || "");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const doPatch = patchFn || api.questMgrPatchQuestion;

  const save = async () => {
    setSaving(true);
    try {
      await doPatch(eventId, q.id, {
        prompt, explanation, correct_option_id: correct,
        options: options.map((o) => ({ id: o.id, text: o.text })),
      });
      toast.success(`${q.qcode} saved`);
      setEditing(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the question.");
    } finally { setSaving(false); }
  };

  const cancel = () => {
    setEditing(false);
    setPrompt(q.prompt);
    setOptions(q.options.map((o) => ({ ...o })));
    setCorrect(q.correct_option_id);
    setExplanation(q.explanation || "");
  };

  return (
    <div className="premium-card p-4" data-testid="quest-admin-question">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--secondary))]">{q.qcode}</div>
        {locked ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(38_45%_38%)]"><Lock className="h-3.5 w-3.5" /> {lockLabel || "Locked (round opened)"}</span>
        ) : !editing ? (
          <button onClick={() => setEditing(true)} data-testid="quest-admin-question-edit" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--secondary))]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
        ) : null}
      </div>

      {!editing ? (
        <>
          <p className="text-sm text-[hsl(var(--primary))] font-medium whitespace-pre-wrap">{prompt}</p>
          <ul className="mt-2 space-y-1">
            {options.map((o) => (
              <li key={o.id} className={`text-sm flex items-start gap-2 ${o.id === correct ? "text-[hsl(142_45%_32%)] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}>
                {o.id === correct ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <span className="h-3.5 w-3.5 inline-block shrink-0" />}
                <span className="text-[10px] font-bold w-4 shrink-0">{o.letter}</span><span>{o.text}</span>
              </li>
            ))}
          </ul>
          {explanation && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic whitespace-pre-wrap">{explanation}</p>}
        </>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-xs" htmlFor={`sc-${q.id}`}>Scenario / question</Label>
            <textarea id={`sc-${q.id}`} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              data-testid="quest-admin-question-prompt" rows={4}
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] p-2 text-sm leading-snug" />
          </div>
          <div>
            <Label className="text-xs">Answer options <span className="font-normal text-[hsl(var(--muted-foreground))]">(tap a letter to mark the correct answer)</span></Label>
            <div className="space-y-2 mt-1">
              {options.map((o, i) => (
                <div key={o.id} className="flex items-center gap-2">
                  <button
                    onClick={() => setCorrect(o.id)}
                    data-testid="quest-admin-correct-toggle"
                    className={`shrink-0 h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${o.id === correct ? "bg-[hsl(142_45%_40%)] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                    title="Mark correct"
                  >{o.letter}</button>
                  <Input value={o.text} onChange={(e) => {
                    const n = [...options]; n[i] = { ...o, text: e.target.value }; setOptions(n);
                  }} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs" htmlFor={`ex-${q.id}`}>Explanation <span className="font-normal text-[hsl(var(--muted-foreground))]">(hidden from attendees until the competition closes)</span></Label>
            <textarea id={`ex-${q.id}`} value={explanation} onChange={(e) => setExplanation(e.target.value)}
              data-testid="quest-admin-question-explanation" rows={3}
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] p-2 text-sm leading-snug" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={save} disabled={saving} data-testid="quest-admin-question-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
            <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TiebreakQuestions({ eventId }) {
  const [tb, setTb] = useState(null);
  const [state, setState] = useState("loading");
  const [repairing, setRepairing] = useState(false);

  const load = useCallback(() => {
    if (!eventId) return;
    setState("loading");
    api.questMgrTbQuestions(eventId)
      .then((d) => { setTb(d); setState("ok"); })
      .catch(() => setState("error"));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const repair = async () => {
    setRepairing(true);
    try {
      const r = await api.questMgrTbQuestionsRepair(eventId);
      if (r.inserted > 0) toast.success(`Added ${r.inserted} missing tie-break question(s)`);
      else toast.info("All tie-break questions are already present");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't repair tie-break questions.");
    } finally { setRepairing(false); }
  };

  const missing = tb ? Math.max(0, (tb.expected || 0) - (tb.present || 0)) : 0;

  return (
    <div data-testid="quest-admin-tiebreak-questions" className="pt-2 border-t border-[hsl(var(--border))]">
      <div className="flex items-center justify-between gap-2 mb-1 mt-4">
        <div className="flex items-center gap-2">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Tie-break questions</h3>
          {tb?.locked && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(38_45%_38%)]">
              <Lock className="h-3.5 w-3.5" /> Locked
            </span>
          )}
        </div>
        <button onClick={load} data-testid="quest-admin-tiebreak-reload"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--secondary))]">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
        A separate round of {tb?.expected || 5} questions ({tb?.points_per_question || 1} point each) used only to
        order tied participants. Editable until the tie-break opens. These never appear in the main 20.
      </p>

      {tb?.locked && tb?.lock_reason && (
        <div className="mb-3 rounded-lg bg-[hsl(38_70%_95%)] text-[hsl(38_55%_30%)] text-xs px-3 py-2 flex items-start gap-2" data-testid="quest-admin-tiebreak-lock-reason">
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {tb.lock_reason}
        </div>
      )}

      {state === "loading" && (
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-tiebreak-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tie-break questions…
        </div>
      )}
      {state === "error" && (
        <div className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Couldn&apos;t load tie-break questions.
          <button onClick={load} className="underline font-semibold">Retry</button>
        </div>
      )}

      {state === "ok" && tb && (
        <>
          {missing > 0 && (
            <div className="mb-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.5)] px-3 py-3" data-testid="quest-admin-tiebreak-repair">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(38_55%_38%)]" />
                <div>
                  <div className="font-semibold text-[hsl(var(--primary))]">{missing} tie-break question(s) missing</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{tb.present} of {tb.expected} present. Repair inserts only the missing ones — existing content and all settings are preserved.</div>
                </div>
              </div>
              {!tb.locked && (
                <Button size="sm" className="mt-2" onClick={repair} disabled={repairing} data-testid="quest-admin-tiebreak-repair-btn">
                  {repairing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Insert missing tie-break questions
                </Button>
              )}
            </div>
          )}

          {tb.questions.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-tiebreak-empty">No tie-break questions present yet.</p>
          ) : (
            <div className="space-y-3">
              {tb.questions.map((q) => (
                <QuestionEditor key={q.id} eventId={eventId} q={q} locked={!!tb.locked}
                  patchFn={api.questMgrTbPatchQuestion} lockLabel="Locked (tie-break opened)" onSaved={load} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


export default function QuestAdmin() {
  const { event } = useAdminEvent();
  const navigate = useNavigate();
  const eventId = event?.id;
  const isPreview = !!(event?.is_preview_test_event) || eventId === "pc-quest-preview"
    || (event?.name || "").toUpperCase().includes("PREVIEW");
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("setup");
  const [comp, setComp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [standings, setStandings] = useState(null);
  const [showTech, setShowTech] = useState(false);

  // --- Shared gamification settings (single source of truth: legacy settings
  // endpoint). Loaded/saved per selected event only, with stale-response guard.
  const [gamSettings, setGamSettings] = useState(null);   // { enabled, attendee_visibility_enabled } | null
  const [gamErr, setGamErr] = useState(null);
  const [gamSavingField, setGamSavingField] = useState(null);
  const gamReqRef = useRef(0);           // increments on each (event/load) to drop stale responses

  const loadGamSettings = useCallback((targetEventId) => {
    const token = ++gamReqRef.current;
    setGamErr(null);
    api.gamSettings(targetEventId)
      .then((s) => {
        // Drop if a newer request started OR the selected event changed.
        if (token !== gamReqRef.current) return;
        setGamSettings({
          enabled: !!s.enabled,
          attendee_visibility_enabled: !!s.attendee_visibility_enabled,
        });
      })
      .catch(() => {
        if (token !== gamReqRef.current) return;
        setGamSettings(null);
        setGamErr("Couldn't load shared gamification settings.");
      });
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setState("loading");
    api.questMgrFull(eventId)
      .then((d) => {
        if (d && d.configured === false) { setData(d); setComp(null); setState("unconfigured"); return; }
        setData(d); setComp(d.competition); setState("ok");
      })
      .catch((e) => setState(e?.response?.status === 403 ? "denied" : "error"));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // Reset ALL pending shared-settings form state on event switch, then reload
  // for the newly selected event. The token bump invalidates any in-flight
  // response from the previous event so it can never be applied/saved here.
  useEffect(() => {
    gamReqRef.current++;          // invalidate any in-flight response
    setGamSettings(null);
    setGamErr(null);
    setGamSavingField(null);
    if (eventId) loadGamSettings(eventId);
  }, [eventId, loadGamSettings]);

  useEffect(() => {
    setStandings(null);
    if (tab === "standings" && eventId) api.questMgrStandings(eventId).then(setStandings).catch(() => {});
  }, [tab, eventId]);

  // Save ONE shared setting for the currently selected event only.
  const saveGamSetting = async (field, value) => {
    const forEvent = eventId;               // capture the event this save targets
    const token = ++gamReqRef.current;      // any later event switch invalidates this
    setGamSavingField(field);
    setGamErr(null);
    try {
      const s = await api.gamUpdateSettings(forEvent, { [field]: value });
      // Ignore if the selected event changed while saving — never apply to another event.
      if (token !== gamReqRef.current || forEvent !== eventId) return;
      setGamSettings({
        enabled: !!s.enabled,
        attendee_visibility_enabled: !!s.attendee_visibility_enabled,
      });
      toast.success(field === "enabled"
        ? (value ? "Gamification enabled for this event" : "Gamification disabled for this event")
        : (value ? "Attendee visibility enabled" : "Attendee visibility disabled"));
      // Quest's derived "master switch" depends on these — refresh quest view.
      load();
    } catch (err) {
      if (forEvent !== eventId) return;
      const msg = err?.response?.data?.detail || "Couldn't save shared setting.";
      setGamErr(msg);
      toast.error(msg);
    } finally {
      if (forEvent === eventId) setGamSavingField(null);
    }
  };

  const patch = async (payload, msg) => {
    setSaving(true);
    try {
      const d = await api.questMgrPatch(eventId, payload);
      setData(d); setComp(d.competition);
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const saveSetup = () => patch({
    title: comp.title, subtitle: comp.subtitle, close_at: comp.close_at || null,
    rounds: comp.rounds.map((r) => ({ round_no: r.round_no, title: r.title, opens_at: r.opens_at || null })),
  }, "Quest settings saved");

  const exportCsv = async () => {
    try {
      const blob = await api.questMgrStandingsCsv(eventId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "pc-quest-standings.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  if (state === "loading") return <div className="premium-card p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--secondary))]" /></div>;
  if (state === "denied") return <Card className="text-center" data-testid="quest-admin-denied"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(38_70%_92%)] text-[hsl(38_55%_32%)] mb-3"><AlertTriangle className="h-6 w-6" /></div><p className="font-serif text-lg text-[hsl(var(--primary))]">You don&apos;t have manager access to this event</p><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Your account isn&apos;t a manager for {event?.name || "this event"}. Choose an event you manage from the event selector.</p></Card>;
  if (state === "unconfigured") return <Card className="text-center" data-testid="quest-admin-unconfigured"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] mb-3"><Brain className="h-6 w-6" /></div><p className="font-serif text-lg text-[hsl(var(--primary))]">PC Quest isn&apos;t set up for this event yet</p><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-md mx-auto">There&apos;s no PC Quest competition configured for {event?.name || "this event"}. It stays unavailable to attendees until an administrator completes the one-time setup. No action is taken automatically.</p><button onClick={load} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--muted))]" data-testid="quest-admin-unconfigured-recheck"><RefreshCw className="h-4 w-4" /> Re-check</button></Card>;
  if (state === "error") return <Card className="text-center"><p>Couldn&apos;t load PC Quest.</p><button onClick={load} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold"><RefreshCw className="h-4 w-4" /> Retry</button></Card>;

  const lc = comp.lifecycle || { state: "draft", label: "Draft", detail: "", missing_config: [], visibility_notes: [], visible_to_attendees: false };
  const published = comp.status === "PUBLISHED";

  return (
    <div data-testid="quest-admin">
      {/* Event context + preview banner (P8) */}
      {isPreview && (
        <div data-testid="quest-admin-preview-banner" className="mb-3 flex items-start gap-2 rounded-lg border border-[hsl(265_45%_60%)] bg-[hsl(265_50%_96%)] px-4 py-2.5 text-sm text-[hsl(265_45%_38%)]">
          <FlaskConical className="h-4 w-4 shrink-0 mt-0.5" />
          <span><b>PREVIEW TEST EVENT.</b> Every change here affects only the isolated <code>{eventId}</code> fixture — never the real PC2026 event.</span>
        </div>
      )}
      <div className="mb-2 flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-event-context">
        <Calendar className="h-3.5 w-3.5" /> Managing: <span className="font-semibold text-[hsl(var(--primary))]">{event?.name || eventId}</span>
      </div>

      {/* Simplified intro + prominent lock warning (P7) */}
      <PageIntro>
        Manage questions, round dates, prizes and results for PC Quest. Each correct answer earns 100 points, up to 2,000.
      </PageIntro>
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-[hsl(38_60%_70%)] bg-[hsl(38_70%_95%)] px-4 py-2.5 text-sm text-[hsl(38_55%_30%)]" data-testid="quest-admin-lock-warning">
        <Lock className="h-4 w-4 shrink-0 mt-0.5" />
        <span>Questions lock permanently when their round first opens. Prize settings, rules and exclusions lock when Round 1 first opens.</span>
      </div>
      <div className="mb-5">
        <button onClick={() => setShowTech((v) => !v)} data-testid="quest-admin-tech-toggle"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
          {showTech ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Technical details
        </button>
        {showTech && (
          <div className="mt-2 rounded-lg bg-[hsl(var(--muted)/0.5)] p-3 text-xs text-[hsl(var(--muted-foreground))] space-y-2" data-testid="quest-admin-tech-details">
            <div>
              Isolated individual competition <code>{comp.competition_id}</code>. Scores live on their own leaderboard and
              never mix with legacy, manual or badge points. Event timezone: <b>{comp.event_timezone}</b> ({TZ_LABEL}).
            </div>
            <div className="pt-2 border-t border-[hsl(var(--border))]">
              <button
                type="button"
                onClick={() => navigate("/manager/gamification")}
                data-testid="quest-admin-legacy-history-link"
                className="inline-flex items-center gap-1.5 font-semibold text-[hsl(var(--secondary))] hover:underline">
                <History className="h-3.5 w-3.5" /> Legacy gamification management
                <ExternalLink className="h-3 w-3" />
              </button>
              <div className="mt-1">
                Older challenges, badges and activity records. This opens the legacy management screen, where editing controls remain available. Manage PC Quest using the tabs above.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lifecycle + status row (P2) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <StatCard label="State" value={lc.label} icon={Brain} accent="gold" sub={lc.visible_to_attendees ? "Visible to attendees" : "Not visible to attendees"} />
        <StatCard label="Master switch" value={comp.global_gamification_on ? "On" : "Off"} icon={ShieldCheck} sub="Global gamification" />
        <StatCard label="Max score" value={data.max_score} icon={Trophy} accent="gold" sub={`${data.points_per_question} per question`} />
        <StatCard label="Closes" value={comp.close_at ? ((lc.state === "closed" || lc.state === "finalised") ? "Closed" : "Scheduled") : "Not set"} icon={Clock} sub={comp.close_at ? fmtLocal(comp.close_at) : "No deadline"} />
      </div>

      <div className={`mb-5 rounded-xl p-4 ${LIFECYCLE_STYLE[lc.state] || LIFECYCLE_STYLE.draft}`} data-testid="quest-admin-lifecycle">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/50" data-testid="quest-admin-lifecycle-state">{lc.label}</span>
          <span className="text-sm font-medium">{lc.detail}</span>
        </div>
        {lc.visibility_notes && lc.visibility_notes.length > 0 && (
          <div className="mt-2 text-xs opacity-90">Visibility: {lc.visibility_notes.join(" · ")}</div>
        )}
        {lc.missing_config && lc.missing_config.length > 0 && (
          <div className="mt-2" data-testid="quest-admin-missing-config">
            <div className="text-xs font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Still to configure:</div>
            <ul className="mt-1 text-xs list-disc pl-5 space-y-0.5">
              {lc.missing_config.map((mc, i) => <li key={i}>{mc}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-5 border-b border-[hsl(var(--border))]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`quest-admin-tab-${t.key}`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.key ? "border-[hsl(var(--secondary))] text-[hsl(var(--primary))]" : "border-transparent text-[hsl(var(--muted-foreground))]"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "setup" && (
        <div className="space-y-4 max-w-2xl">
          <Card>
            <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-3">Visibility &amp; lifecycle</h3>
            <div className="space-y-3">
              <ToggleRow label="Published" hint="Draft keeps everything hidden. Publish when the bank is final." checked={published}
                onChange={(v) => patch({ status: v ? "PUBLISHED" : "DRAFT" }, v ? "Published" : "Back to draft")} testid="quest-admin-published" />
              <ToggleRow label="Visible to attendees" hint="The attendee PC Quest surface appears only when this is on (and the master switch is on). This does not open any round — rounds open on their dates." checked={!!comp.visible}
                onChange={(v) => patch({ visible: v }, v ? "Visible" : "Hidden")} testid="quest-admin-visible" />
              <ToggleRow label="Paused" hint="Pause blocks new answers but never unlocks opened rounds." checked={!!comp.paused}
                onChange={(v) => patch({ paused: v }, v ? "Paused" : "Resumed")} testid="quest-admin-paused" />
            </div>
            {!comp.global_gamification_on && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-[hsl(38_70%_94%)] p-3 text-xs text-[hsl(38_50%_30%)]">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> The global gamification master switch is OFF, so attendees cannot see PC Quest regardless of these settings. Turn both shared settings on below.
              </div>
            )}
          </Card>

          {/* Shared gamification settings — single source of truth (legacy
              settings), surfaced here because PC Quest depends on them. Kept
              visually and functionally distinct from the Quest toggles above. */}
          <Card>
            <div className="flex items-start gap-2 mb-1">
              <Power className="h-4 w-4 mt-1 text-[hsl(var(--secondary))]" />
              <div>
                <h3 className="font-serif text-lg text-[hsl(var(--primary))]">Shared gamification settings</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Event-wide switches shared with the gamification module. PC Quest needs <b>both</b> ON to be visible to attendees.
                </p>
              </div>
            </div>

            {gamSettings === null && !gamErr && (
              <div className="py-4 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-gam-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading shared settings…
              </div>
            )}

            {gamErr && gamSettings === null && (
              <div className="py-3 flex items-center justify-between gap-2 rounded-lg bg-[hsl(0_70%_96%)] px-3 text-sm text-[hsl(0_60%_38%)]" data-testid="quest-admin-gam-error">
                <span className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {gamErr}</span>
                <button onClick={() => loadGamSettings(eventId)} className="inline-flex items-center gap-1 font-semibold hover:underline"><RefreshCw className="h-3.5 w-3.5" /> Retry</button>
              </div>
            )}

            {gamSettings && (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-[hsl(var(--primary))] flex items-center gap-1.5">
                      Gamification enabled <span className="text-[10px] uppercase tracking-wide font-bold text-[hsl(var(--secondary))]">Event master</span>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Master power for the whole gamification engine on this event. PC Quest needs this ON. Turning it OFF also hides PC Quest.
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {gamSavingField === "enabled" && <Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--muted-foreground))]" />}
                    <Switch checked={!!gamSettings.enabled} disabled={!!gamSavingField}
                      onCheckedChange={(v) => saveGamSetting("enabled", v)} data-testid="quest-admin-gam-enabled" />
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 pt-3 border-t border-[hsl(var(--border))]">
                  <div>
                    <div className="text-sm font-medium text-[hsl(var(--primary))]">Attendee gamification visibility</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Lets attendees see gamification surfaces for this event. PC Quest needs this ON too (with the master above) to appear.
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {gamSavingField === "attendee_visibility_enabled" && <Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--muted-foreground))]" />}
                    <Switch checked={!!gamSettings.attendee_visibility_enabled} disabled={!!gamSavingField}
                      onCheckedChange={(v) => saveGamSetting("attendee_visibility_enabled", v)} data-testid="quest-admin-gam-visibility" />
                  </div>
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-[hsl(214_70%_96%)] p-3 text-xs text-[hsl(214_55%_34%)]" data-testid="quest-admin-gam-note">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    These are shared, event-wide switches — separate from Quest&apos;s <b>Published</b>, <b>Visible to attendees</b> and <b>Paused</b> settings above.
                    Turning them on does <b>not</b> publish PC Quest or open a round (rounds open on their dates), and does <b>not</b> override the
                    legacy challenge feature gate — legacy challenges stay hidden while their feature is off.
                  </span>
                </div>
                {gamErr && (
                  <div className="flex items-center gap-1.5 text-xs text-[hsl(0_60%_42%)]" data-testid="quest-admin-gam-inline-error">
                    <AlertTriangle className="h-3.5 w-3.5" /> {gamErr}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card>
            <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-1">Details &amp; schedule</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-1"><Info className="h-3.5 w-3.5" /> All dates and times are entered and shown in <b>{TZ_LABEL}</b> ({comp.event_timezone}).</p>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={comp.title || ""} onChange={(e) => setComp({ ...comp, title: e.target.value })} data-testid="quest-admin-title" /></div>
              <div><Label>Subtitle</Label><Input value={comp.subtitle || ""} onChange={(e) => setComp({ ...comp, subtitle: e.target.value })} /></div>
              <div>
                <Label>Final closing deadline <span className="text-[hsl(var(--muted-foreground))] font-normal">(leave blank until confirmed)</span></Label>
                <Input type="datetime-local" value={comp.close_at || ""} onChange={(e) => setComp({ ...comp, close_at: e.target.value })} data-testid="quest-admin-close-at" />
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  {comp.close_at ? <>Closes <b>{fmtLocal(comp.close_at)}</b>. </> : "No deadline set. "}
                  Correct answers &amp; explanations reveal to attendees only after this time.
                </p>
              </div>
              {comp.rounds.map((r, i) => {
                // Display-only status mapping (does not touch scheduling/lock logic).
                // Locked rounds keep their permanent lock label below. For unlocked
                // rounds: no opening date -> "Not scheduled"; a (future) date -> the
                // backend state ("Scheduled"/"Open"/"Closed").
                const stateLabel = { open: "Open", closed: "Closed", scheduled: "Scheduled" }[r.state] || r.state;
                const roundStatusLabel = (!r.opens_at && r.state === "scheduled") ? "Not scheduled" : stateLabel;
                return (
                <div key={r.round_no} className="rounded-lg border border-[hsl(var(--border))] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm text-[hsl(var(--primary))]">Round {r.round_no} <span className="text-[hsl(var(--muted-foreground))] font-normal">· Categories {r.category_nos.join(" & ")}</span></div>
                    {r.locked ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(38_45%_38%)]"><Lock className="h-3.5 w-3.5" /> Locked (round opened)</span>
                      : <span className="text-[11px] text-[hsl(var(--muted-foreground))]" data-testid={`quest-admin-round-${r.round_no}-status`}>{roundStatusLabel}</span>}
                  </div>
                  <Label className="text-xs">Opens at {r.locked && <span className="text-[hsl(var(--muted-foreground))]">(content already locked)</span>}</Label>
                  <Input type="datetime-local" value={r.opens_at || ""} data-testid={`quest-admin-round-${r.round_no}-opens`}
                    onChange={(e) => { const rounds = [...comp.rounds]; rounds[i] = { ...r, opens_at: e.target.value }; setComp({ ...comp, rounds }); }} />
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{r.opens_at ? <>Opens <b>{fmtLocal(r.opens_at)}</b></> : <>Opening date to be announced</>}</p>
                </div>
                );
              })}
            </div>
            <Button className="mt-4" onClick={saveSetup} disabled={saving} data-testid="quest-admin-save-setup">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save settings</Button>
          </Card>
        </div>
      )}

      {tab === "questions" && (
        <div className="space-y-6">
          {data.categories.map((c) => (
            <div key={c.category_no}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Category {c.category_no}: {c.title}</h3>
                {c.locked && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(38_45%_38%)]"><Lock className="h-3.5 w-3.5" /> Locked (round opened)</span>}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{c.subtitle}</p>
              <div className="space-y-3">
                {c.questions.map((q) => (
                  <QuestionEditor key={q.id} eventId={eventId} q={q} locked={c.locked} onSaved={load} />
                ))}
              </div>
            </div>
          ))}
          <TiebreakQuestions eventId={eventId} />
        </div>
      )}

      {tab === "finalize" && (
        <FinalizeTab key={eventId} eventId={eventId} comp={comp} patch={patch} reload={load} showTech={showTech} />
      )}

      {tab === "standings" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{standings ? `${standings.participant_count} participant(s)` : "Loading…"} {standings ? (standings.closed ? "· Closed" : "· Provisional (competition still open)") : ""}</p>
            <Button size="sm" variant="secondary" onClick={exportCsv} data-testid="quest-admin-export"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          </div>
          {!standings ? <div className="premium-card p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--secondary))]" /></div>
            : standings.rows.length === 0 ? <Card className="text-center text-[hsl(var(--muted-foreground))]">No scores yet.</Card>
              : (
                <div className="premium-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                      <th className="px-4 py-3">Rank</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Points</th><th className="px-4 py-3">Answered</th><th className="px-4 py-3">Correct</th>
                    </tr></thead>
                    <tbody>
                      {standings.rows.map((r) => (
                        <tr key={r.attendee_id} data-testid="quest-standings-row" className="border-b border-[hsl(var(--border))] last:border-0">
                          <td className="px-4 py-3 font-bold text-[hsl(var(--secondary))]">{r.rank}</td>
                          <td className="px-4 py-3 font-medium text-[hsl(var(--primary))]">{r.display_name}</td>
                          <td className="px-4 py-3 font-semibold">{r.points}</td>
                          <td className="px-4 py-3 text-[hsl(var(--muted-foreground))]">{r.answered}/20</td>
                          <td className="px-4 py-3 text-[hsl(var(--muted-foreground))]">{r.correct}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-standings-note">
            {standings?.closed
              ? "Final ordering, tie-breaks and winner declaration are managed on the Prizes & finalise tab."
              : "These standings are provisional and update live as attendees answer. Tie-breaks and winner declaration become available after the competition closes — see the Prizes & finalise tab."}
          </p>
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-medium text-[hsl(var(--primary))]">{label}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// --- Event-scoped searchable attendee picker (P3) --------------------------
function AttendeePicker({ eventId, excludeIds, value, onChange, testid }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.questMgrAttendees(eventId).then((d) => setList(d.attendees || [])).catch(() => {});
  }, [eventId]);
  const selected = list.find((a) => a.attendee_id === value) || null;
  const needle = q.trim().toLowerCase();
  const filtered = list
    .filter((a) => !excludeIds.includes(a.attendee_id))
    .filter((a) => !needle || `${a.name} ${a.email}`.toLowerCase().includes(needle))
    .slice(0, 8);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--secondary))] bg-[hsl(var(--secondary)/0.08)] px-3 py-2" data-testid={`${testid}-selected`}>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[hsl(var(--primary))] truncate">{selected.name}</div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{selected.email}</div>
        </div>
        <button onClick={() => { onChange(null); setQ(""); }} className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive,0_60%_50%))]" title="Clear"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-2.5">
        <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        <input
          value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search attendees by name or email"
          data-testid={testid}
          className="flex-1 bg-transparent py-2 text-sm outline-none"
        />
      </div>
      {open && needle && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-white shadow-lg max-h-60 overflow-auto" data-testid={`${testid}-results`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">No matching attendees.</div>
          ) : filtered.map((a) => (
            <button key={a.attendee_id} onClick={() => { onChange(a.attendee_id); setOpen(false); setQ(""); }}
              data-testid={`${testid}-option`}
              className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--muted)/0.6)] border-b border-[hsl(var(--border))] last:border-0">
              <div className="text-sm font-medium text-[hsl(var(--primary))]">{a.name}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{a.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Guided external-draw recorder for one unresolved tied group (P4) ------
function DrawGroup({ group, onRecord }) {
  const members = group.members || group.attendee_ids.map((id) => ({ attendee_id: id, display_name: id }));
  const [order, setOrder] = useState([]);
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = (id) => {
    if (busy) return; // no edits mid-save
    setOrder((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const complete = order.length === members.length;
  const canRecord = complete && notes.trim() && confirm;

  const record = async () => {
    if (busy) return; // guard against duplicate clicks while saving
    setBusy(true); setErr(null);
    try {
      await onRecord({ attendee_ids: members.map((m) => m.attendee_id), ordered_outcome: order, notes: notes.trim() });
      setOrder([]); setNotes(""); setConfirm(false);
    } catch (e) {
      setErr(e?.message || "Couldn't record draw."); // keep order + notes intact
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-[hsl(var(--border))] p-3" data-testid="quest-admin-draw-group">
      <div className="text-sm font-medium text-[hsl(var(--primary))]">Tie at {group.main_points} pts · positions {group.positions ? group.positions.join("–") : ""}</div>
      <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Tap participants in the externally determined order, winner first. The app never chooses an outcome.</p>
      <div className="space-y-1.5">
        {members.map((m) => {
          const pos = order.indexOf(m.attendee_id);
          const ranked = pos >= 0;
          return (
            <button key={m.attendee_id} onClick={() => toggle(m.attendee_id)} data-testid="quest-admin-draw-member"
              className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left ${ranked ? "border-[hsl(var(--secondary))] bg-[hsl(var(--secondary)/0.08)]" : "border-[hsl(var(--border))]"}`}>
              <span className={`h-6 w-6 shrink-0 grid place-items-center rounded-full text-xs font-bold ${ranked ? "bg-[hsl(var(--secondary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{ranked ? pos + 1 : "–"}</span>
              <span className="text-sm text-[hsl(var(--primary))]">{m.display_name}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{complete ? "Order complete" : `${order.length}/${members.length} ranked`}</span>
        {order.length > 0 && <button onClick={() => setOrder([])} className="text-[11px] font-semibold text-[hsl(var(--secondary))]" data-testid="quest-admin-draw-reset">Reset order</button>}
      </div>
      <div className="mt-2">
        <Label className="text-xs">Notes <span className="font-normal text-[hsl(var(--muted-foreground))]">(how/where the draw was conducted — required)</span></Label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="quest-admin-draw-notes"
          className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] p-2 text-sm" />
      </div>
      <label className="flex items-center gap-2 text-xs mt-2">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="quest-admin-draw-confirm" />
        I confirm this order was determined externally and recorded accurately.
      </label>
      {err && (
        <div className="mt-2 flex items-start gap-2 rounded-lg bg-[hsl(0_70%_96%)] border border-[hsl(0_60%_80%)] p-2 text-xs text-[hsl(0_55%_38%)]" data-testid="quest-admin-draw-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {err}
        </div>
      )}
      <Button size="sm" className="mt-2" onClick={record} disabled={!canRecord || busy} data-testid="quest-admin-draw-record">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} {busy ? "Recording…" : "Record draw outcome"}
      </Button>
    </div>
  );
}

function FinalizeTab({ eventId, comp, patch, reload, showTech }) {
  const locked = comp.prize_rules_locked;
  const [winnerCount, setWinnerCount] = useState(comp.winner_count || "");
  const [prizes, setPrizes] = useState(() => {
    const m = {}; (comp.prizes || []).forEach((p) => { m[p.position] = p.description; }); return m;
  });
  const [rules, setRules] = useState((comp.rules || []).join("\n"));
  const [exAid, setExAid] = useState(null); const [exReason, setExReason] = useState("");
  const [prep, setPrep] = useState(null);
  const [tbStatus, setTbStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tbOpens, setTbOpens] = useState(""); const [tbClose, setTbClose] = useState("");
  const [ackFewer, setAckFewer] = useState(false);
  const [snap, setSnap] = useState(null);

  const loadPrep = () => api.questMgrTbPrepare(eventId).then(setPrep).catch(() => setPrep(null));
  const loadStatus = () => api.questMgrTbStatus(eventId).then(setTbStatus).catch(() => {});
  const loadPreview = () => api.questMgrFinalizePreview(eventId).then(setPreview).catch(() => toast.error("Couldn't load finalisation preview."));
  const loadSnap = () => api.questMgrFinalSnapshot(eventId).then(setSnap).catch(() => {});
  useEffect(() => { loadPrep(); loadStatus(); loadPreview(); loadSnap(); /* eslint-disable-next-line */ }, [eventId, comp.finalized]);

  const n = Number(winnerCount) || 0;
  const savePrizes = async () => {
    const list = [];
    for (let i = 1; i <= n; i++) list.push({ position: i, description: prizes[i] || "" });
    await patch({ winner_count: n || null, prizes: list,
      rules: rules.split("\n").map((s) => s.trim()).filter(Boolean) }, "Prize settings saved");
    loadPreview();
  };
  const addExclusion = async () => {
    if (!exAid) { toast.error("Choose an attendee to exclude."); return; }
    if (!exReason.trim()) { toast.error("A reason is required."); return; }
    try {
      await api.questMgrSetExclusion(eventId, { attendee_id: exAid, reason: exReason.trim() });
      toast.success("Exclusion added"); setExAid(null); setExReason(""); reload(); loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add exclusion."); }
  };
  const removeExclusion = async (aid) => {
    try { await api.questMgrSetExclusion(eventId, { attendee_id: aid, remove: true }); reload(); loadPreview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };
  const assign = async (scoreValues) => {
    if (!tbOpens || !tbClose) { toast.error("Set a common opening and closing time."); return; }
    try { await api.questMgrTbAssign(eventId, { score_values: scoreValues, opens_at: tbOpens, close_at: tbClose });
      toast.success("Tie-break assigned"); loadPrep(); loadStatus(); loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign."); }
  };
  const recordDraw = async (payload) => {
    try {
      await api.questMgrDraw(eventId, payload);
      toast.success("Draw recorded"); reload(); loadPreview();
    } catch (e) {
      // Reconcile with server state before any retry: an uncertain failure (e.g.
      // a 502 after the write) may have actually recorded the draw. Refreshing
      // reflects the true state so the group disappears if it was saved, instead
      // of inviting a duplicate submission.
      reload(); loadPreview();
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail
        || (status === 502 || !e?.response
            ? "The server didn't confirm the draw. We've refreshed the latest state — check whether it was recorded before trying again."
            : "Couldn't record draw.");
      throw new Error(msg);
    }
  };
  const doFinalize = async () => {
    try { await api.questMgrFinalize(eventId, { acknowledge_fewer: ackFewer });
      toast.success("Competition finalised"); reload(); loadSnap(); loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't finalise."); }
  };
  const doReopen = async () => {
    const reason = window.prompt("Reason for reopening (prior result will be superseded):");
    if (!reason) return;
    try { await api.questMgrReopen(eventId, { reason }); toast.success("Reopened"); reload(); loadSnap(); loadPreview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reopen."); }
  };
  const exportFinal = async () => {
    try { const blob = await api.questMgrFinalCsv(eventId); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "pc-quest-final.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const excludedIds = useMemo(() => (comp.exclusions || []).map((e) => e.attendee_id), [comp.exclusions]);
  const unresolved = preview?.unresolved_ties || [];

  return (
    <div className="space-y-4 max-w-2xl" data-testid="quest-admin-finalize">
      {locked && (
        <div className="rounded-lg bg-[hsl(38_70%_94%)] p-3 text-xs text-[hsl(38_50%_30%)] flex items-start gap-2">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" /> Round 1 has opened, so prize settings, rules and exclusions are locked.
        </div>
      )}
      {/* Prize config */}
      <Card>
        <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-3">Prizes &amp; rules</h3>
        <Label>Winner count</Label>
        <div className="flex gap-2 mt-1 mb-3">
          {[2, 3].map((v) => (
            <button key={v} disabled={locked} data-testid={`quest-admin-winner-${v}`} onClick={() => setWinnerCount(v)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold border ${Number(winnerCount) === v ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>{v}</button>
          ))}
          <span className="text-xs text-[hsl(var(--muted-foreground))] self-center">{!winnerCount && "Unset"}</span>
        </div>
        {Array.from({ length: n }, (_, i) => i + 1).map((pos) => (
          <div key={pos} className="mb-2">
            <Label className="text-xs">Position {pos} prize</Label>
            <Input disabled={locked} value={prizes[pos] || ""} data-testid={`quest-admin-prize-${pos}`}
              onChange={(e) => setPrizes({ ...prizes, [pos]: e.target.value })} placeholder={`Prize for position ${pos}`} />
          </div>
        ))}
        <Label className="text-xs mt-2">Published rules (one per line)</Label>
        <textarea disabled={locked} value={rules} onChange={(e) => setRules(e.target.value)} rows={4}
          data-testid="quest-admin-rules"
          className="w-full rounded-lg border border-[hsl(var(--border))] p-2 text-sm mt-1 disabled:opacity-60" />
        <Button className="mt-3" disabled={locked} onClick={savePrizes} data-testid="quest-admin-save-prizes"><Save className="h-4 w-4 mr-1.5" /> Save prizes &amp; rules</Button>
      </Card>

      {/* Exclusions (P3) */}
      <Card>
        <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-1">Prize exclusions</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Exclude testers or question authors from winning, with a reason. Every change is audited and cannot be changed once Round 1 opens.</p>
        <div className="space-y-1.5 mb-3">
          {(comp.exclusions || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">None.</div>}
          {(comp.exclusions || []).map((e) => (
            <div key={e.attendee_id} data-testid="quest-admin-exclusion-row" className="flex items-start justify-between gap-2 text-sm rounded-lg bg-[hsl(var(--muted)/0.5)] px-3 py-2">
              <div className="min-w-0">
                <div className="font-semibold text-[hsl(var(--primary))] truncate">{e.name || e.attendee_id}</div>
                {e.email && <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{e.email}</div>}
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Reason: {e.reason}</div>
                {(e.excluded_by || e.excluded_at) && (
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">By {e.excluded_by || "—"}{e.excluded_at ? ` · ${fmtInstant(e.excluded_at)}` : ""}</div>
                )}
              </div>
              {!locked && <button onClick={() => removeExclusion(e.attendee_id)} className="shrink-0 text-xs text-[hsl(var(--destructive,0_60%_50%))] font-semibold">Remove</button>}
            </div>
          ))}
        </div>
        {!locked && (
          <div className="space-y-2 border-t border-[hsl(var(--border))] pt-3">
            <div><Label className="text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Attendee to exclude</Label>
              <div className="mt-1"><AttendeePicker eventId={eventId} excludeIds={excludedIds} value={exAid} onChange={setExAid} testid="quest-admin-exclusion-picker" /></div>
            </div>
            <div><Label className="text-xs">Reason</Label><Input value={exReason} onChange={(e) => setExReason(e.target.value)} data-testid="quest-admin-exclusion-reason" placeholder="e.g. Quiz author / internal tester" /></div>
            <Button size="sm" onClick={addExclusion} disabled={!exAid || !exReason.trim()} data-testid="quest-admin-exclusion-add">Add exclusion</Button>
          </div>
        )}
      </Card>

      {/* Tie-break */}
      <Card>
        <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-1">Tie-break</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Only decides the order of participants on the same main score. Tie-break points never change the 2,000 main total. All times are {TZ_LABEL}.</p>
        {!prep ? <div className="text-sm text-[hsl(var(--muted-foreground))]">Close the main competition to prepare a tie-break.</div> : (
          <>
            {prep.affected_groups.length === 0 ? (
              <div className="text-sm text-[hsl(142_40%_32%)] flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> No prize-affecting ties.</div>
            ) : (
              <div className="space-y-2">
                {prep.affected_groups.map((g) => (
                  <div key={g.main_points} data-testid="quest-admin-tie-group" className="rounded-lg border border-[hsl(var(--border))] p-3">
                    <div className="text-sm font-medium text-[hsl(var(--primary))]">Tie at {g.main_points} pts (positions from {g.min_position})</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{g.members.map((m) => m.display_name).join(", ")}</div>
                    <div className="mt-1 text-[11px]">{g.assigned ? <span className="text-[hsl(142_40%_32%)]">Assigned</span> : <span className="text-[hsl(38_45%_38%)]">Not assigned</span>}</div>
                  </div>
                ))}
                {(comp.finalized || (tbStatus && tbStatus.assigned_count > 0)) ? (
                  <div className="rounded-lg bg-[hsl(var(--muted)/0.5)] p-3 text-xs" data-testid="quest-admin-tb-locked">
                    <div className="flex items-center gap-1.5 font-semibold text-[hsl(var(--primary))]">
                      <Lock className="h-3.5 w-3.5" /> Tie-break assigned — window locked{tbStatus?.state === "closed" ? " · Closed" : ""}
                    </div>
                    <div className="mt-1 text-[hsl(var(--muted-foreground))]">
                      {tbStatus?.opens_at ? <>Opened <b>{fmtLocal(tbStatus.opens_at)}</b></> : "Opening time not set"}
                      {tbStatus?.close_at ? <> · Closed <b>{fmtLocal(tbStatus.close_at)}</b></> : null}
                    </div>
                    <div className="mt-1 text-[hsl(var(--muted-foreground))]">Reassignment is disabled{comp.finalized ? " (results finalised)" : " once the tie-break has opened/closed"} to protect the recorded outcome.</div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 items-end pt-1">
                    <div><Label className="text-xs">Opens ({TZ_LABEL})</Label><Input type="datetime-local" value={tbOpens} onChange={(e) => setTbOpens(e.target.value)} data-testid="quest-admin-tb-opens" /></div>
                    <div><Label className="text-xs">Closes ({TZ_LABEL})</Label><Input type="datetime-local" value={tbClose} onChange={(e) => setTbClose(e.target.value)} data-testid="quest-admin-tb-close" /></div>
                    <Button size="sm" onClick={() => assign(prep.affected_groups.map((g) => g.main_points))} data-testid="quest-admin-tb-assign">Assign all tied groups</Button>
                  </div>
                )}
              </div>
            )}
            {tbStatus && tbStatus.assigned_count > 0 && (
              <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-tb-status">
                Tie-break: <span className="capitalize">{tbStatus.state}</span> · {tbStatus.assigned_count} assigned
                {tbStatus.opens_at && <> · opens {fmtLocal(tbStatus.opens_at)}</>}
                {tbStatus.close_at && <> · closes {fmtLocal(tbStatus.close_at)}</>}
                {tbStatus.reveal && <span> · scores revealed</span>}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Draw (P4) */}
      <Card>
        <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-1">Record an external draw</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Used only if a prize-affecting tie still remains after the tie-break has closed. The app never picks a winner automatically.</p>
        {preview && !preview.draws_enabled ? (
          <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--muted)/0.5)] p-3 text-sm text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-draw-disabled">
            <Info className="h-4 w-4 shrink-0 mt-0.5" /> {preview.draw_block_reason || "Draw recording is not available yet."}
          </div>
        ) : unresolved.length === 0 ? (
          <div className="text-sm text-[hsl(142_40%_32%)] flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> No draw needed.</div>
        ) : (
          <div className="space-y-3">
            {unresolved.map((g) => (
              <DrawGroup key={g.attendee_ids.join("-")} group={g} onRecord={recordDraw} />
            ))}
          </div>
        )}
        {(comp.draws || []).length > 0 && (
          <div className="mt-3 border-t border-[hsl(var(--border))] pt-2 space-y-1" data-testid="quest-admin-draw-history">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Recorded draws</div>
            {(comp.draws || []).map((d) => (
              <div key={d.id} className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="quest-admin-draw-record-row">
                <span className="text-[hsl(var(--primary))] font-medium">{(d.ordered_names && d.ordered_names.length ? d.ordered_names : d.ordered_outcome).join(" → ")}</span>
                {" · "}by {d.conducted_by || "—"} · {fmtInstant(d.conducted_at)}
                {d.notes ? <span className="italic"> — {d.notes}</span> : null}
                {showTech && <span className="block text-[10px] opacity-70">IDs: {d.ordered_outcome.join(" → ")}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Finalise */}
      <Card>
        <h3 className="font-serif text-lg text-[hsl(var(--primary))] mb-2">Finalise</h3>
        {comp.finalized ? (
          <div data-testid="quest-admin-finalized">
            <div className="flex items-center gap-2 text-[hsl(142_40%_32%)] font-semibold mb-2"><Trophy className="h-4 w-4" /> Finalised.</div>
            {snap && snap.winners && (
              <ol className="space-y-1 mb-3">
                {snap.winners.map((w) => <li key={w.position} className="text-sm"><b>#{w.position}</b> {w.display_name} — {w.prize} <span className="text-[hsl(var(--muted-foreground))]">({w.main_points} pts)</span></li>)}
              </ol>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={exportFinal} data-testid="quest-admin-final-export"><Download className="h-4 w-4 mr-1.5" /> Export final CSV</Button>
              <Button size="sm" variant="ghost" onClick={doReopen} data-testid="quest-admin-reopen">Reopen (supersede)</Button>
            </div>
          </div>
        ) : !preview ? <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading preview…</div> : (
          <div data-testid="quest-admin-finalize-preview">
            {preview.blockers.length > 0 ? (
              <div className="space-y-1 mb-3">
                {preview.blockers.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[hsl(38_50%_32%)]"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{b}</div>
                ))}
              </div>
            ) : (
              <>
                <div className="text-sm font-medium text-[hsl(var(--primary))] mb-1">Proposed winners (provisional):</div>
                <ol className="space-y-1 mb-3">
                  {preview.proposed_winners.map((w) => <li key={w.position} className="text-sm"><b>#{w.position}</b> {w.display_name} — {w.prize} <span className="text-[hsl(var(--muted-foreground))]">({w.main_points} main{w.tiebreak_points ? `, ${w.tiebreak_points} tie-break` : ""})</span></li>)}
                </ol>
              </>
            )}
            {preview.warnings.map((w, i) => <div key={i} className="text-xs text-[hsl(38_50%_32%)] mb-2">{w}</div>)}
            {preview.fewer_eligible && (
              <label className="flex items-center gap-2 text-xs mb-2"><input type="checkbox" checked={ackFewer} onChange={(e) => setAckFewer(e.target.checked)} data-testid="quest-admin-ack-fewer" /> Confirm finalising with fewer winners than prize positions</label>
            )}
            <Button onClick={doFinalize} disabled={preview.blockers.length > 0} data-testid="quest-admin-finalize-btn"><Trophy className="h-4 w-4 mr-1.5" /> Finalise &amp; confirm winners</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
